import { estimateTransitSeconds, haversineDistanceMeters } from './job-plan-time';

export interface SequenceStopInput {
  id?: number;
  jobId: number;
  durationSeconds: number;
  latitude?: number | null;
  longitude?: number | null;
  windowStartOffsetSeconds?: number | null;
  windowEndOffsetSeconds?: number | null;
  plannedArrival?: number | null;
  plannedDeparture?: number | null;
  driveSecondsFromPrevious?: number | null;
}

export interface RecomputedStopTiming {
  jobId: number;
  position: number;
  driveStartSeconds: number;
  plannedArrivalSeconds: number;
  serviceStartSeconds: number;
  plannedDepartureSeconds: number;
  driveSecondsFromPrevious: number;
  driveDistanceMetersFromPrevious: number;
  waitingSeconds: number;
  windowViolation: boolean;
  windowViolationReason: string | null;
}

export interface SequenceFeasibility {
  stops: RecomputedStopTiming[];
  shiftStartSeconds: number;
  shiftEndSeconds: number;
  shiftDurationSeconds: number;
  totalServiceSeconds: number;
  totalDriveSeconds: number;
  totalDistanceMeters: number;
  totalWaitingSeconds: number;
  totalBreakSeconds: number;
  returnDriveSeconds: number;
  returnDriveDistanceMeters: number;
  returnDriveStartSeconds: number | null;
  returnDriveEndSeconds: number | null;
  firstJobStartSeconds: number | null;
  lastJobEndSeconds: number | null;
  dayFinishSeconds: number | null;
  isOverShift: boolean;
  overrunSeconds: number;
  firstOffendingStopIndex: number | null;
  breakStartOffsetSeconds: number | null;
  breakEndOffsetSeconds: number | null;
  hasBreakOverlap: boolean;
  hasTimeBlockOverlap: boolean;
  productivityRate: number;
  utilizationRate: number;
}

export interface SequenceOptions {
  shiftStartOffsetSeconds?: number;
  shiftEndOffsetSeconds?: number;
  breakDurationSeconds?: number;
  breakWindowStartOffsetSeconds?: number;
  breakWindowEndOffsetSeconds?: number;
  startLocation?: { latitude?: number | null; longitude?: number | null } | null;
  endLocation?: { latitude?: number | null; longitude?: number | null } | null;
  timeBlocks?: Array<{ startOffsetSeconds: number; endOffsetSeconds: number }>;
}

/**
 * Recomputes planned times and feasibility for a sequence of stops assigned to an operator.
 * Accounts for estimated travel times between coordinates and slots operator breaks dynamically.
 */
export function recomputeStopSequence(
  stops: SequenceStopInput[],
  options: SequenceOptions = {}
): SequenceFeasibility {
  const shiftStart = options.shiftStartOffsetSeconds ?? 28800; // 08:00
  const shiftEnd = options.shiftEndOffsetSeconds ?? 61200; // 17:00
  const shiftDuration = Math.max(0, shiftEnd - shiftStart);

  let currentTime = shiftStart;
  let totalService = 0;
  let totalDrive = 0;
  let totalDistanceMeters = 0;
  let totalWaiting = 0;
  let totalBreak = 0;
  let isOverShift = false;
  let firstOffendingStopIndex: number | null = null;
  let breakStartOffsetSeconds: number | null = null;
  let breakEndOffsetSeconds: number | null = null;
  let breakPlaced = false;
  let returnDriveSeconds = 0;
  let returnDriveDistanceMeters = 0;
  let returnDriveStartSeconds: number | null = null;
  let returnDriveEndSeconds: number | null = null;
  const recomputedStops: RecomputedStopTiming[] = [];

  let prevCoord = options.startLocation ?? null;

  for (let i = 0; i < stops.length; i++) {
    const stop = stops[i];
    const duration = Math.max(0, stop.durationSeconds);
    const driveStart = currentTime;

    let driveFromPrev = stop.driveSecondsFromPrevious ?? 0;
    let driveDistFromPrev = 0;

    if (
      prevCoord?.latitude != null &&
      prevCoord?.longitude != null &&
      stop.latitude != null &&
      stop.longitude != null
    ) {
      driveDistFromPrev =
        haversineDistanceMeters(
          prevCoord.latitude,
          prevCoord.longitude,
          stop.latitude,
          stop.longitude
        ) * 1.3;
    }

    if (driveFromPrev === 0) {
      if (
        prevCoord?.latitude != null &&
        prevCoord?.longitude != null &&
        stop.latitude != null &&
        stop.longitude != null
      ) {
        driveFromPrev = estimateTransitSeconds(
          prevCoord.latitude,
          prevCoord.longitude,
          stop.latitude,
          stop.longitude
        );
      }
    }

    totalDrive += driveFromPrev;
    totalDistanceMeters += driveDistFromPrev;
    let plannedArrival = driveStart + driveFromPrev;

    // Dynamic break placement if needed
    if (
      !breakPlaced &&
      options.breakDurationSeconds != null &&
      options.breakDurationSeconds > 0 &&
      options.breakWindowStartOffsetSeconds != null &&
      options.breakWindowEndOffsetSeconds != null
    ) {
      if (plannedArrival >= options.breakWindowStartOffsetSeconds) {
        breakStartOffsetSeconds = plannedArrival;
        breakEndOffsetSeconds = plannedArrival + options.breakDurationSeconds;
        totalBreak += options.breakDurationSeconds;
        plannedArrival += options.breakDurationSeconds;
        breakPlaced = true;
      }
    }

    // Wait when arriving before a service window opens; later stops begin after the wait.
    let waitingSeconds = 0;
    let serviceStart = plannedArrival;
    let windowViolation = false;
    let windowViolationReason: string | null = null;

    if (stop.windowStartOffsetSeconds != null && plannedArrival < stop.windowStartOffsetSeconds) {
      waitingSeconds = stop.windowStartOffsetSeconds - plannedArrival;
      serviceStart = stop.windowStartOffsetSeconds;
      totalWaiting += waitingSeconds;
    } else if (
      stop.windowEndOffsetSeconds != null &&
      plannedArrival > stop.windowEndOffsetSeconds
    ) {
      windowViolation = true;
      const diffMins = Math.round((plannedArrival - stop.windowEndOffsetSeconds) / 60);
      windowViolationReason = `Arrives ${diffMins}m after window closes`;
    }

    const plannedDeparture = serviceStart + duration;
    totalService += duration;
    currentTime = plannedDeparture;

    if (stop.latitude != null && stop.longitude != null) {
      prevCoord = { latitude: stop.latitude, longitude: stop.longitude };
    }

    if (plannedDeparture > shiftEnd && !isOverShift) {
      isOverShift = true;
      firstOffendingStopIndex = i;
    }

    recomputedStops.push({
      jobId: stop.jobId,
      position: i,
      driveStartSeconds: driveStart,
      plannedArrivalSeconds: plannedArrival,
      serviceStartSeconds: serviceStart,
      plannedDepartureSeconds: plannedDeparture,
      driveSecondsFromPrevious: driveFromPrev,
      driveDistanceMetersFromPrevious: driveDistFromPrev,
      waitingSeconds,
      windowViolation,
      windowViolationReason,
    });
  }

  // Transit back to end depot if configured
  if (
    stops.length > 0 &&
    options.endLocation?.latitude != null &&
    options.endLocation?.longitude != null &&
    prevCoord?.latitude != null &&
    prevCoord?.longitude != null
  ) {
    returnDriveSeconds = estimateTransitSeconds(
      prevCoord.latitude,
      prevCoord.longitude,
      options.endLocation.latitude,
      options.endLocation.longitude
    );
    returnDriveDistanceMeters =
      haversineDistanceMeters(
        prevCoord.latitude,
        prevCoord.longitude,
        options.endLocation.latitude,
        options.endLocation.longitude
      ) * 1.3;
    returnDriveStartSeconds = currentTime;
    returnDriveEndSeconds = currentTime + returnDriveSeconds;
    totalDrive += returnDriveSeconds;
    totalDistanceMeters += returnDriveDistanceMeters;
    currentTime += returnDriveSeconds;
    if (currentTime > shiftEnd && !isOverShift) {
      isOverShift = true;
    }
  }

  const overrunSeconds = Math.max(0, currentTime - shiftEnd);

  // Time blocks overlap check
  let hasTimeBlockOverlap = false;
  const timeBlocks = options.timeBlocks ?? [];
  for (const block of timeBlocks) {
    for (const stop of recomputedStops) {
      if (
        stop.serviceStartSeconds < block.endOffsetSeconds &&
        stop.plannedDepartureSeconds > block.startOffsetSeconds
      ) {
        hasTimeBlockOverlap = true;
        break;
      }
    }
  }

  // Break window overlap check
  let hasBreakOverlap = false;
  if (
    !breakPlaced &&
    options.breakWindowStartOffsetSeconds != null &&
    options.breakWindowEndOffsetSeconds != null
  ) {
    const breakStart = options.breakWindowStartOffsetSeconds;
    const breakEnd = options.breakWindowEndOffsetSeconds;
    for (const stop of recomputedStops) {
      if (stop.serviceStartSeconds < breakEnd && stop.plannedDepartureSeconds > breakStart) {
        hasBreakOverlap = true;
        break;
      }
    }
  }

  const productivityRate = shiftDuration > 0 ? Math.min(1, totalService / shiftDuration) : 0;
  const utilizationRate =
    shiftDuration > 0 ? Math.min(1, (totalService + totalDrive) / shiftDuration) : 0;

  const firstJobStartSeconds = recomputedStops[0]?.serviceStartSeconds ?? null;
  const lastJobEndSeconds =
    recomputedStops.length > 0
      ? recomputedStops[recomputedStops.length - 1].plannedDepartureSeconds
      : null;
  const dayFinishSeconds = returnDriveEndSeconds ?? lastJobEndSeconds;

  return {
    stops: recomputedStops,
    shiftStartSeconds: shiftStart,
    shiftEndSeconds: shiftEnd,
    shiftDurationSeconds: shiftDuration,
    totalServiceSeconds: totalService,
    totalDriveSeconds: totalDrive,
    totalDistanceMeters,
    totalWaitingSeconds: totalWaiting,
    totalBreakSeconds: totalBreak,
    returnDriveSeconds,
    returnDriveDistanceMeters,
    returnDriveStartSeconds,
    returnDriveEndSeconds,
    firstJobStartSeconds,
    lastJobEndSeconds,
    dayFinishSeconds,
    isOverShift,
    overrunSeconds,
    firstOffendingStopIndex,
    breakStartOffsetSeconds,
    breakEndOffsetSeconds,
    hasBreakOverlap,
    hasTimeBlockOverlap,
    productivityRate,
    utilizationRate,
  };
}
